import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ContentCard from 'ext-ui/components/ContentCard/ContentCard'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import CheckmarkCircleIcon from 'ext-ui/svg/icon/CheckmarkCircleIcon'
import LinkIcon from 'ext-ui/svg/icon/LinkIcon'
import moment from 'moment'
import React, { KeyboardEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import { SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  CreditCardStatus,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { useWhitelabel } from 'src/utils/whitelabel.utils'

import style from './VirtualCardCapabilitiesPanel.style'

type VirtualCardCapabilitiesPanelProps = {
  className?: string
  creditCard: CreditCardListModel
}

const determineInitialConnectedStatus = (
  creditCard: CreditCardListModel,
): boolean => {
  if (
    creditCard.partner.virtualCardsSupported === true &&
    creditCard.partner.status !== CreditCardStatus.Active
  ) {
    return false
  }
  return Boolean(
    creditCard.partner.virtualCardsSupported &&
      creditCard.partner.status === CreditCardStatus.Active,
  )
}

export default function VirtualCardCapabilitiesPanel(
  props: VirtualCardCapabilitiesPanelProps,
) {
  const { className = '', creditCard } = props
  // State hooks
  const [isConnected, setIsConnected] = useState<boolean>(
    determineInitialConnectedStatus(creditCard),
  )
  // TODO: Check this logic and fix when requirements and design are
  // finalized
  const [showDisconnectAlertPanel, _setShowDisconnectAlertPanel] =
    useState<boolean>(false)
  const [showConnectModal, setShowConnectModal] = useState<boolean>(false)
  const [isVerifying, setIsVerifying] = useState<boolean>(false)

  // Custom hooks
  const { whiteLabelIncludes } = useWhitelabel()
  const translate = useTranslate()
  const dispatch = useDispatch()

  const shouldDarkenPanel = whiteLabelIncludes([
    SupportedBrands.Boc,
    SupportedBrands.Pnc,
    SupportedBrands.Bmo,
    SupportedBrands.Corpay,
    SupportedBrands.PacWest,
  ])

  if (
    whiteLabelIncludes(SupportedBrands.Jpm) ||
    creditCard.displayStatus === CreditCardDisplayStatus.Closed
  ) {
    return null
  }

  // For the time being as darkened variants are coming and not in Figma, let's just
  // hide the darkened state and not show the panel at all to keep consistent
  if (shouldDarkenPanel) {
    return null
  }

  if (
    !creditCard.partner?.virtualCardsSupported ||
    creditCard.physicalCard.status === null ||
    creditCard.physicalCard.status === PhysicalCardStatus.NotApplicable
  ) {
    return null
  }

  const handleConnectClick = () => {
    setShowConnectModal(true)
  }

  return (
    <div
      className={cn('VirtualCardCapabilitiesPanel', className, {
        darkened: shouldDarkenPanel,
        'has-top-alert-panel': showDisconnectAlertPanel,
      })}
      css={style}
    >
      {showDisconnectAlertPanel && (
        <AlertPanel variant="warning">
          <div className="flex-row gap-4 align-top">
            <AlertTriangleYellowIcon />
            <div className="flex-column gap-2">
              <p className="font-bold">
                {translate('Disconnected Integration')}
              </p>
              <p className="font-regular">
                {'PLACEHOLDER TEXT - CHANGE WHEN FINALIZED'}
              </p>
            </div>
          </div>
        </AlertPanel>
      )}
      <ContentCard variant={shouldDarkenPanel ? 'darkened' : 'standard'}>
        <div className="flex-column gap-5">
          <div className="flex-row justify-start gap-2">
            {isConnected && <CheckmarkCircleIcon />}
            <p className="font-h3">
              {translate(
                isConnected
                  ? 'Virtual Card Capabilities Enabled'
                  : 'Enable Virtual Card Capabilities',
              )}
            </p>
          </div>

          <p className="font-regular">
            {translate(
              'Enable virtual card capabilities to create and manage virtual cards for this account.',
            )}
          </p>
        </div>
      </ContentCard>
      <div className="VirtualCardCapabilitiesPanel-bottom">
        {/* TODO: Remove this condition and render normally when disconnect logic BE is ready */}
        {!isConnected && (
          <>
            <div
              className="linktext-content"
              role="button"
              aria-label={translate('Enable/Disable Virtual Card Capabilities')}
              onClick={() => {
                if (isConnected) {
                  // TODO: disconnect
                } else {
                  handleConnectClick()
                }
              }}
              tabIndex={0}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  if (isConnected) {
                    // TODO: disconnect
                  } else {
                    handleConnectClick()
                  }
                }
              }}
            >
              <div className="flex-row align-center gap-2">
                {!isConnected && <LinkIcon />}
                <p
                  className={cn('font-linktext connect-text', {
                    'disconnect-text': isConnected,
                  })}
                >
                  {translate(isConnected ? 'Disconnect' : 'Connect')}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Connect/Disconnect Modals */}

      {/* Connect Modal Here */}
      <ConnectPhysicalCardModal
        isOpen={showConnectModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConnectModal(false)
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        reviewEnrollCardProps={{
          onBack: () => {
            setShowConnectModal(false)
          },
          creditCardId: creditCard.id,
          onVerifyStart: () => {
            setIsVerifying(true)
          },
          onVerifyComplete: () => {
            setIsVerifying(false)
            setShowConnectModal(false)

            // TODO: Edit toast message for virtual
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <p>
                    {translate(
                      'Success! Virtual card capabilities are now enabled.',
                    )}
                  </p>
                ),
              }),
            )
            setIsConnected(true)
            //
          },
          onVerifyError: () => {
            setIsVerifying(false)
            // TODO: Add error handling here when we get a more robust backend
          },
          submissionData: {
            binData: {
              id: creditCard.issuerId,
              // This is specifically for the VIRTUAL_ONLY Flow for now
              eligibility: 'VIRTUAL_ONLY',
              issuerLogoImg: creditCard.cardImage.urls.small,
              issuerRequiresVerification: false,
            },
            addressData: {
              cardName: creditCard.displayName,
              ...creditCard.address,
            },
            cardData: {
              // We only have access to the last 4 and not the CVC in this view
              // So I think this might be our best bet in getting this to work
              // The component itself already masks the card number minus the last
              // 4 digits so this should be alright
              number: `**** **** **** ${creditCard.last4}`,
              // We are unable to get CVC info from the card detail view since
              // this data lives in VGS and it may be a nasty security issue
              // to display it here if a user's account is compromised
              cvc: '***',
              expiration: moment(creditCard.expires).format('MM/YY'),
            },
          },
        }}
      />
    </div>
  )
}
